import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  csvPfad,
  kartenPfad,
  useTokenAusgabe,
  useVeranstaltung,
} from '../../admin/veranstaltungenApi';
import { kopiereText } from '../../browser';

/**
 * Token-Ausgabe im Klartext (§7.9, §8.1).
 *
 * Die **einzige** Ansicht, in der vollständige Tokens erscheinen. Sie wird erst auf ausdrückliche
 * Anforderung geladen, jeder Abruf erzeugt einen Protokolleintrag, und die Werte werden nicht
 * zwischengespeichert.
 *
 * Gesperrte und ersetzte Tokens fehlen hier: §8.1 verlangt, dass sie in neu erzeugten
 * Verteilungslisten nicht mehr auftauchen. Seit ÄA-7 gilt dasselbe für bereits per E-Mail
 * versendete Tokens — sonst könnte derselbe Token bei einer hybriden Veranstaltung zusätzlich
 * physisch ausgegeben werden.
 */
export function TokenAusgabeSeite(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const [angefordert, setAngefordert] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  const [kopierFehler, setKopierFehler] = useState(false);

  const veranstaltung = useVeranstaltung(id);
  const ausgabe = useTokenAusgabe(id, angefordert);

  async function kopiereAlle(): Promise<void> {
    const text = (ausgabe.data ?? []).map((eintrag) => eintrag.token).join('\n');

    // Über `kopiereText()`: ohne sicheren Kontext — etwa beim Zugriff über `http://<IP>:3250` —
    // gibt es `navigator.clipboard` nicht, und die Schaltfläche wirkte bisher tot.
    const gelungen = await kopiereText(text);
    setKopiert(gelungen);
    setKopierFehler(!gelungen);
    window.setTimeout(() => {
      setKopiert(false);
      setKopierFehler(false);
    }, 3000);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Token-Ausgabe</h1>
          <p className="text-text-gedaempft text-sm">
            {veranstaltung.data?.bezeichnung ?? '…'}
            {veranstaltung.data !== undefined && ` · Code ${veranstaltung.data.code}`}
          </p>
        </div>
        <Link
          to=".."
          relative="path"
          className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
        >
          Zur Tokenliste
        </Link>
      </div>

      <section className="border-warnung/50 bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-2 text-base font-semibold">Vertraulich</h2>
        <p className="text-text-gedaempft text-sm">
          Diese Seite zeigt vollständige Teilnehmer-Tokens. Jeder Token ist das einzige
          Anmeldemerkmal einer stimmberechtigten Person. Jeder Abruf wird im Änderungsprotokoll
          vermerkt. Die Anzeige nicht offen stehen lassen und nichts davon ungeschützt ablegen oder
          versenden.
        </p>
      </section>

      {!angefordert ? (
        <button
          type="button"
          onClick={() => {
            setAngefordert(true);
          }}
          className="bg-akzent text-akzent-text transition hover:brightness-110 min-h-touchflaeche self-start rounded-md px-4 font-medium"
        >
          Tokens anzeigen
        </button>
      ) : (
        <>
          {ausgabe.isPending && <p className="text-text-gedaempft text-sm">Wird geladen …</p>}

          {ausgabe.isError && (
            <p role="alert" className="text-fehler text-sm font-medium">
              {ausgabe.error.message}
            </p>
          )}

          {ausgabe.data !== undefined && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    void kopiereAlle();
                  }}
                  disabled={ausgabe.data.length === 0}
                  className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand rounded-md border px-4 text-sm font-medium disabled:pointer-events-none disabled:opacity-60"
                >
                  {kopiert ? 'Kopiert' : 'Alle kopieren'}
                </button>
                {kopierFehler && (
                  <span role="alert" className="text-warnung text-sm font-medium">
                    Das Kopieren hat der Browser abgelehnt. Bitte den CSV-Download verwenden.
                  </span>
                )}
                <a
                  href={csvPfad(id)}
                  className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-4 text-sm font-medium"
                >
                  CSV herunterladen
                </a>
                {/* §8.3: eine DIN-A6-Karte je Token, mit QR-Code zum Anmelden. */}
                <a
                  href={kartenPfad(id)}
                  className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-4 text-sm font-medium"
                >
                  A6-Karten herunterladen
                </a>
                <span className="text-text-gedaempft text-sm">
                  {ausgabe.data.length} gültige Token(s). Gesperrte, ersetzte und bereits per E-Mail
                  versendete sind nicht enthalten.
                </span>
              </div>

              {ausgabe.data.length === 0 ? (
                <p className="border-rand bg-flaeche text-text-gedaempft rounded-xl border p-4 text-sm">
                  Es sind keine gültigen Tokens vorhanden.
                </p>
              ) : (
                <ul className="border-rand bg-flaeche grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-2 rounded-xl border p-4 font-mono text-sm shadow-sm">
                  {ausgabe.data.map((eintrag) => (
                    <li key={eintrag.id} className="bg-flaeche-gedaempft rounded px-2 py-1">
                      {eintrag.token}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
